import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Download, Share2, X } from 'lucide-react';
import { useFocusTrap, useIsMobileLike } from '../hooks';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import styles from './ImagePreviewModal.module.css';

const DISMISS_THRESHOLD = 100;
const VELOCITY_THRESHOLD = 0.5;
const CONTROLS_TIMEOUT = 3000;
const DOUBLE_TAP_DELAY = 300;
const SUPPRESS_CLICK_DELAY = 300;
const CLOSE_ANIMATION_MS = 200;
const MAX_ZOOM = 4;
const MIN_ZOOM = 1;
const EDGE_PADDING_RATIO = 0.20;
const EDGE_PADDING_MAX = 250;
const GESTURE_TAP_SLOP = 8;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const clampIndex = (value, length) => {
  if (length <= 0 || Number.isNaN(value)) return 0;
  return Math.min(Math.max(value, 0), length - 1);
};
const getImageUrl = (image) => {
  if (!image) return null;
  if (typeof image === 'string') return image;
  return image.url || null;
};
const getImageName = (image) => {
  if (!image || typeof image === 'string') return null;
  return image.name || null;
};
const getTouchDistance = (touches) => {
  const [first, second] = touches;
  if (!first || !second) return 0;
  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
};
const getTouchCenter = (touches) => {
  const [first, second] = touches;
  if (!first || !second) return { x: 0, y: 0 };
  return {
    x: (first.clientX + second.clientX) / 2,
    y: (first.clientY + second.clientY) / 2,
  };
};

const ImagePreviewModal = function ImagePreviewModal({
  imageUrl,
  images = [],
  initialIndex = 0,
  onClose,
  headerContent,
  footerActions,
  ariaLabel,
  shareTitle,
  autoHideControls = true,
}) {
  const { t } = useTranslation();

  const imageList = useMemo(() => {
    if (Array.isArray(images) && images.length > 0) return images;
    if (imageUrl) return [{ url: imageUrl }];
    return [];
  }, [images, imageUrl]);

  const [currentIndex, setCurrentIndex] = useState(() =>
    clampIndex(initialIndex, imageList.length)
  );
  const [scale, setScaleState] = useState(1);
  const [panOffset, setPanOffsetState] = useState({ x: 0, y: 0 });
  const [showControls, setShowControls] = useState(true);
  const [controlsEpoch, setControlsEpoch] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [backdropOpacity, setBackdropOpacity] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const currentImage = imageList[currentIndex];
  const currentImageUrl = getImageUrl(currentImage) || imageUrl;
  const currentImageName = getImageName(currentImage);
  const hasMultipleImages = imageList.length > 1;
  const isZoomed = scale > 1;
  const isOpen = Boolean(currentImageUrl);
  const isChromeHidden = !showControls;

  const chromeTabIndex = isChromeHidden ? -1 : undefined;
  const chromeA11yProps = useMemo(
    () => ({
      inert: isChromeHidden ? '' : undefined,
      'aria-hidden': isChromeHidden ? true : undefined,
    }),
    [isChromeHidden]
  );

  const containerRef = useFocusTrap(isOpen);
  const imageRef = useRef(null);
  const topBarRef = useRef(null);
  const bottomBarRef = useRef(null);
  const navPrevRef = useRef(null);
  const navNextRef = useRef(null);
  const lastTapRef = useRef(0);
  const startPosRef = useRef({ x: 0, y: 0 });
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastPosRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef(0);
  const dragModeRef = useRef(null);
  const panStartRef = useRef({ x: 0, y: 0, originX: 0, originY: 0 });
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const isClosingRef = useRef(false);
  const activePointerIdRef = useRef(null);
  const lastInteractionRef = useRef('pointer');
  const scaleRef = useRef(scale);
  const movedRef = useRef(false);
  const pendingTapRef = useRef(false);
  const suppressClickUntilRef = useRef(0);
  const controlsTimeoutRef = useRef(null);
  const doubleTapTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const pinchRef = useRef({
    active: false,
    startDistance: 0,
    startScale: 1,
    origin: { x: 0, y: 0 },
    point: { x: 0, y: 0 },
    startCenter: { x: 0, y: 0 },
  });

  const isMobile = useIsMobileLike();

  useBodyScrollLock(isOpen);

  const focusContainer = useCallback(() => {
    if (!containerRef.current) return;
    containerRef.current.focus({ preventScroll: true });
  }, [containerRef]);

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const footerActionNodes =
    typeof footerActions === 'function'
      ? footerActions({ chromeHidden: isChromeHidden, tabIndex: chromeTabIndex })
      : null;

  // Keep refs + state aligned. Use sync setters only; do not call raw setters directly.
  const setScaleSync = useCallback((next) => {
    const value = typeof next === 'function' ? next(scaleRef.current) : next;
    scaleRef.current = value;
    setScaleState(value);
    return value;
  }, []);

  const setPanOffsetSync = useCallback((next) => {
    const value = typeof next === 'function' ? next(panOffsetRef.current) : next;
    panOffsetRef.current = value;
    setPanOffsetState(value);
    return value;
  }, []);

  const setIsDraggingSync = useCallback((next) => {
    const value = typeof next === 'function' ? next(isDraggingRef.current) : next;
    isDraggingRef.current = value;
    setIsDragging(value);
    return value;
  }, []);

  const noteInteraction = useCallback((type, { show = true } = {}) => {
    lastInteractionRef.current = type;
    if (show) {
      setShowControls(true);
    }
    setControlsEpoch((prev) => prev + 1);
  }, []);

  const requestClose = useCallback(
    (_reason, { exitOffset } = {}) => {
      if (isClosingRef.current) return;

      if (prefersReducedMotion) {
        onClose();
        return;
      }

      isClosingRef.current = true;
      setIsClosing(true);
      setIsDraggingSync(false);
      setDragOffset(exitOffset || { x: 0, y: 0 });
      setBackdropOpacity(0);

      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }

      closeTimeoutRef.current = window.setTimeout(() => {
        closeTimeoutRef.current = null;
        onClose();
      }, CLOSE_ANIMATION_MS);
    },
    [onClose, prefersReducedMotion, setIsDraggingSync]
  );

  const goToPrev = useCallback(() => {
    if (imageList.length === 0) return;
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setShowControls(true);
  }, [imageList.length]);

  const goToNext = useCallback(() => {
    if (imageList.length === 0) return;
    setCurrentIndex((prev) => Math.min(imageList.length - 1, prev + 1));
    setShowControls(true);
  }, [imageList.length]);

  useEffect(() => {
    setCurrentIndex((prev) => clampIndex(prev, imageList.length));
  }, [imageList.length]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(media.matches);
    updatePreference();

    if (media.addEventListener) {
      media.addEventListener('change', updatePreference);
      return () => media.removeEventListener('change', updatePreference);
    }

    media.addListener(updatePreference);
    return () => media.removeListener(updatePreference);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setShowControls(true);
    }
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (isClosingRef.current) return;
    setScaleSync(1);
    setPanOffsetSync({ x: 0, y: 0 });
    setDragOffset({ x: 0, y: 0 });
    setBackdropOpacity(1);
    setIsClosing(false);
    setImageLoaded(false);
    setShowControls(true);
  }, [currentImageUrl, setPanOffsetSync, setScaleSync]);

  const getPanBoundsForScale = useCallback((nextScale) => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) return { maxX: 0, maxY: 0 };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const baseWidth = image.clientWidth;
    const baseHeight = image.clientHeight;

    if (!baseWidth || !baseHeight) return { maxX: 0, maxY: 0 };

    const scaledWidth = baseWidth * nextScale;
    const scaledHeight = baseHeight * nextScale;

    const overflowX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const overflowY = Math.max(0, (scaledHeight - containerHeight) / 2);
    const edgePaddingX =
      overflowX > 0 ? Math.min(containerWidth * EDGE_PADDING_RATIO, EDGE_PADDING_MAX) : 0;
    const edgePaddingY =
      overflowY > 0 ? Math.min(containerHeight * EDGE_PADDING_RATIO, EDGE_PADDING_MAX) : 0;

    return {
      maxX: overflowX + edgePaddingX,
      maxY: overflowY + edgePaddingY,
    };
  }, [containerRef]);

  const clampPanOffset = useCallback(
    (nextScale = scaleRef.current) => {
      if (nextScale <= 1) {
        if (panOffsetRef.current.x !== 0 || panOffsetRef.current.y !== 0) {
          setPanOffsetSync({ x: 0, y: 0 });
        }
        return;
      }

      const { maxX, maxY } = getPanBoundsForScale(nextScale);
      const nextX = clamp(panOffsetRef.current.x, -maxX, maxX);
      const nextY = clamp(panOffsetRef.current.y, -maxY, maxY);

      if (nextX !== panOffsetRef.current.x || nextY !== panOffsetRef.current.y) {
        setPanOffsetSync({ x: nextX, y: nextY });
      }
    },
    [getPanBoundsForScale, setPanOffsetSync]
  );

  useEffect(() => {
    clampPanOffset(scale);
  }, [clampPanOffset, scale]);

  useEffect(() => {
    if (!imageLoaded) return;
    clampPanOffset(scaleRef.current);
  }, [clampPanOffset, imageLoaded]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (typeof window === 'undefined') return undefined;

    const container = containerRef.current;
    const image = imageRef.current;
    if (!container) return undefined;

    let frameId = null;
    const requestClamp = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        clampPanOffset(scaleRef.current);
      });
    };

    const handleWindowResize = () => {
      requestClamp();
    };

    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        requestClamp();
      });
      observer.observe(container);
      if (image) {
        observer.observe(image);
      }
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [clampPanOffset, containerRef, isOpen]);

  useEffect(() => {
    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }

    if (!isOpen) return undefined;
    if (!showControls) return undefined;
    if (!autoHideControls) return undefined;
    if (prefersReducedMotion || isDraggingRef.current) return undefined;
    if (lastInteractionRef.current !== 'pointer') return undefined;

    controlsTimeoutRef.current = window.setTimeout(() => {
      if (
        lastInteractionRef.current === 'pointer' &&
        !isDraggingRef.current &&
        !prefersReducedMotion
      ) {
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
        const isChromeFocused =
          activeElement &&
          ((topBarRef.current && topBarRef.current.contains(activeElement)) ||
            (bottomBarRef.current && bottomBarRef.current.contains(activeElement)) ||
            (navPrevRef.current && navPrevRef.current.contains(activeElement)) ||
            (navNextRef.current && navNextRef.current.contains(activeElement)));

        if (!isChromeFocused) {
          setShowControls(false);
          focusContainer();
        }
      }
    }, CONTROLS_TIMEOUT);

    return () => {
      if (controlsTimeoutRef.current) {
        window.clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
    };
  }, [autoHideControls, controlsEpoch, focusContainer, isOpen, prefersReducedMotion, showControls]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Escape') {
        noteInteraction('keyboard');
      }

      switch (event.key) {
        case 'Escape':
          requestClose('escape');
          break;
        case 'ArrowLeft':
          if (hasMultipleImages && scaleRef.current <= 1) {
            goToPrev();
          }
          break;
        case 'ArrowRight':
          if (hasMultipleImages && scaleRef.current <= 1) {
            goToNext();
          }
          break;
        case '+':
        case '=':
          setScaleSync((prev) => Math.min(prev + 0.5, MAX_ZOOM));
          break;
        case '-':
          setScaleSync((prev) => Math.max(prev - 0.5, MIN_ZOOM));
          break;
        default:
          break;
      }
    },
    [goToNext, goToPrev, hasMultipleImages, noteInteraction, requestClose, setScaleSync]
  );

  const handleControlPointerDown = useCallback(
    (event) => {
      event?.stopPropagation();
      noteInteraction('pointer');
    },
    [noteInteraction]
  );

  const handleTap = useCallback(() => {
    if (prefersReducedMotion) return;
    focusContainer();
    setShowControls((prev) => !prev);
    noteInteraction('pointer', { show: false });
  }, [focusContainer, noteInteraction, prefersReducedMotion]);

  const handleDoubleTap = useCallback(
    (event) => {
      if (suppressClickUntilRef.current > Date.now()) {
        suppressClickUntilRef.current = 0;
        lastTapRef.current = 0;
        if (doubleTapTimeoutRef.current) {
          window.clearTimeout(doubleTapTimeoutRef.current);
          doubleTapTimeoutRef.current = null;
        }
        return;
      }

      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
        event.preventDefault();
        event.stopPropagation();
        if (doubleTapTimeoutRef.current) {
          window.clearTimeout(doubleTapTimeoutRef.current);
          doubleTapTimeoutRef.current = null;
        }

        focusContainer();

        if (scaleRef.current === 1) {
          setScaleSync(2);
          if (!prefersReducedMotion) {
            setShowControls(false);
          } else {
            setShowControls(true);
          }
        } else {
          setScaleSync(1);
          setShowControls(true);
        }

        lastTapRef.current = 0;
        noteInteraction('pointer', { show: false });
        return;
      }

      lastTapRef.current = now;
      if (doubleTapTimeoutRef.current) {
        window.clearTimeout(doubleTapTimeoutRef.current);
      }
      doubleTapTimeoutRef.current = window.setTimeout(() => {
        if (Date.now() - lastTapRef.current >= DOUBLE_TAP_DELAY && lastTapRef.current !== 0) {
          handleTap();
        }
        doubleTapTimeoutRef.current = null;
      }, DOUBLE_TAP_DELAY);
    },
    [focusContainer, handleTap, noteInteraction, prefersReducedMotion, setScaleSync]
  );

  const startDrag = useCallback(() => {
      if (isClosingRef.current) return;

      pendingTapRef.current = false;
      movedRef.current = true;
      noteInteraction('pointer', { show: false });
      setIsDraggingSync(true);
      setDragOffset({ x: 0, y: 0 });
      setBackdropOpacity(1);

      const currentScale = scaleRef.current;
      const { x: startX, y: startY } = startPosRef.current;

      if (currentScale > 1) {
        dragModeRef.current = 'pan';
        panStartRef.current = {
          x: startX,
          y: startY,
          originX: panOffsetRef.current.x,
          originY: panOffsetRef.current.y,
        };
      } else {
        dragModeRef.current = 'dismiss';
      }
    },
    [noteInteraction, setIsDraggingSync]
  );

  const handleDragStart = useCallback(
    (clientX, clientY, { immediateDrag = false } = {}) => {
      if (isClosingRef.current) return;

      focusContainer();
      pendingTapRef.current = true;
      movedRef.current = false;
      dragModeRef.current = null;
      startPosRef.current = { x: clientX, y: clientY };
      lastPosRef.current = { x: clientX, y: clientY };
      lastTimeRef.current = Date.now();
      velocityRef.current = { x: 0, y: 0 };

      if (immediateDrag) {
        startDrag();
      }
    },
    [focusContainer, startDrag]
  );

  const handleDragMove = useCallback(
    (clientX, clientY) => {
      if (!isDraggingRef.current) {
        if (!pendingTapRef.current) return;

        const distanceFromStart = Math.hypot(
          clientX - startPosRef.current.x,
          clientY - startPosRef.current.y
        );

        if (distanceFromStart <= GESTURE_TAP_SLOP) return;

        startDrag();
        if (!isDraggingRef.current) return;
      }

      const now = Date.now();
      const dt = Math.max(now - lastTimeRef.current, 1);

      velocityRef.current = {
        x: (clientX - lastPosRef.current.x) / dt,
        y: (clientY - lastPosRef.current.y) / dt,
      };

      lastPosRef.current = { x: clientX, y: clientY };
      lastTimeRef.current = now;

      if (dragModeRef.current === 'pan') {
        const { maxX, maxY } = getPanBoundsForScale(scaleRef.current);
        const deltaX = clientX - panStartRef.current.x;
        const deltaY = clientY - panStartRef.current.y;
        const nextX = clamp(panStartRef.current.originX + deltaX, -maxX, maxX);
        const nextY = clamp(panStartRef.current.originY + deltaY, -maxY, maxY);
        setPanOffsetSync({ x: nextX, y: nextY });
        return;
      }

      const deltaX = clientX - startPosRef.current.x;
      const deltaY = clientY - startPosRef.current.y;
      const rubberBandX = deltaX * 0.2;

      setDragOffset({ x: rubberBandX, y: deltaY });

      const progress = Math.min(Math.abs(deltaY) / 300, 1);
      setBackdropOpacity(1 - progress * 0.6);
    },
    [getPanBoundsForScale, setPanOffsetSync, startDrag]
  );

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;

    setIsDraggingSync(false);
    pendingTapRef.current = false;

    if (movedRef.current) {
      suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_DELAY;
      movedRef.current = false;
    }

    if (dragModeRef.current === 'pan') {
      dragModeRef.current = null;
      noteInteraction('pointer', { show: false });
      return;
    }

    const deltaX = lastPosRef.current.x - startPosRef.current.x;
    const deltaY = lastPosRef.current.y - startPosRef.current.y;
    const rubberBandX = deltaX * 0.2;

    if (dragModeRef.current === 'dismiss' && hasMultipleImages && scaleRef.current <= 1) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const isHorizontalSwipe = absX > 50 && absX > absY * 1.5;

      if (isHorizontalSwipe) {
        if (deltaX > 0) {
          goToPrev();
        } else if (deltaX < 0) {
          goToNext();
        }

        setDragOffset({ x: 0, y: 0 });
        setBackdropOpacity(1);
        dragModeRef.current = null;
        noteInteraction('pointer', { show: false });
        return;
      }
    }

    const { y: velocityY } = velocityRef.current;

    const shouldDismiss =
      Math.abs(deltaY) > DISMISS_THRESHOLD || Math.abs(velocityY) > VELOCITY_THRESHOLD;

    if (shouldDismiss) {
      const exitY = deltaY > 0 ? window.innerHeight : -window.innerHeight;
      requestClose('drag', { exitOffset: { x: rubberBandX * 2, y: exitY } });
    } else {
      setDragOffset({ x: 0, y: 0 });
      setBackdropOpacity(1);
      noteInteraction('pointer', { show: false });
    }

    dragModeRef.current = null;
  }, [goToNext, goToPrev, hasMultipleImages, noteInteraction, requestClose, setIsDraggingSync]);

  const getContainerCenter = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }, [containerRef]);

  const handlePinchStart = useCallback(
    (touches) => {
      const distance = getTouchDistance(touches);
      if (!distance) return;

      focusContainer();
      pendingTapRef.current = false;
      noteInteraction('pointer', { show: false });
      const center = getTouchCenter(touches);
      const origin = getContainerCenter();
      const startScale = scaleRef.current;
      const startPan = panOffsetRef.current;

      pinchRef.current = {
        active: true,
        startDistance: distance,
        startScale,
        origin,
        point: {
          x: (center.x - origin.x - startPan.x) / startScale,
          y: (center.y - origin.y - startPan.y) / startScale,
        },
        startCenter: center,
      };

      setIsDraggingSync(true);
      movedRef.current = false;
      dragModeRef.current = 'pinch';
      setDragOffset({ x: 0, y: 0 });
      setBackdropOpacity(1);
    },
    [focusContainer, getContainerCenter, noteInteraction, setIsDraggingSync]
  );

  const handlePinchMove = useCallback(
    (touches) => {
      if (!pinchRef.current.active || pinchRef.current.startDistance === 0) return;

      const distance = getTouchDistance(touches);
      if (!distance) return;

      const center = getTouchCenter(touches);
      const { startCenter, startDistance, startScale, origin, point } = pinchRef.current;
      const distanceDelta = Math.abs(distance - startDistance);
      const centerDelta = Math.hypot(center.x - startCenter.x, center.y - startCenter.y);

      if (distanceDelta > GESTURE_TAP_SLOP || centerDelta > GESTURE_TAP_SLOP) {
        movedRef.current = true;
      }

      const nextScale = clamp(startScale * (distance / startDistance), MIN_ZOOM, MAX_ZOOM);
      const nextPan = {
        x: center.x - origin.x - nextScale * point.x,
        y: center.y - origin.y - nextScale * point.y,
      };
      const { maxX, maxY } = getPanBoundsForScale(nextScale);
      const clampedPan = {
        x: clamp(nextPan.x, -maxX, maxX),
        y: clamp(nextPan.y, -maxY, maxY),
      };

      setScaleSync(nextScale);
      setPanOffsetSync(clampedPan);
    },
    [getPanBoundsForScale, setPanOffsetSync, setScaleSync]
  );

  const handlePinchEnd = useCallback(
    (touches) => {
      if (!pinchRef.current.active) return false;

      pinchRef.current.active = false;
      dragModeRef.current = null;

      if (movedRef.current) {
        suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_DELAY;
        movedRef.current = false;
      }

      if (touches.length === 1) {
        const touch = touches[0];
        handleDragStart(touch.clientX, touch.clientY, { immediateDrag: true });
      } else {
        setIsDraggingSync(false);
        noteInteraction('pointer', { show: false });
      }

      return true;
    },
    [handleDragStart, noteInteraction, setIsDraggingSync]
  );

  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      handlePinchStart(event.touches);
      return;
    }

    if (event.touches.length === 1 && !pinchRef.current.active) {
      handleDragStart(event.touches[0].clientX, event.touches[0].clientY);
    }
  };

  const handleTouchMove = (event) => {
    if (pinchRef.current.active && event.touches.length === 2) {
      handlePinchMove(event.touches);
      return;
    }

    if (event.touches.length === 2) {
      handlePinchStart(event.touches);
      return;
    }

    if (event.touches.length === 1 && !pinchRef.current.active) {
      handleDragMove(event.touches[0].clientX, event.touches[0].clientY);
    }
  };

  const handleTouchEnd = (event) => {
    if (handlePinchEnd(event.touches)) {
      pendingTapRef.current = false;
      return;
    }

    handleDragEnd();
    pendingTapRef.current = false;
  };


  const handleDownload = useCallback(async () => {
    if (!currentImageUrl) return;

    if (!isMobile) {
      const link = document.createElement('a');
      link.href = currentImageUrl;
      link.download = currentImageName || currentImageUrl.split('/').pop() || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const hasFileShare =
      typeof navigator !== 'undefined' &&
      typeof navigator.canShare === 'function' &&
      typeof navigator.share === 'function';

    if (hasFileShare) {
      try {
        const response = await fetch(currentImageUrl, { mode: 'cors' });
        const blob = await response.blob();
        const file = new File([blob], currentImageName || 'photo.jpg', {
          type: blob.type || 'image/jpeg',
        });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file] });
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          return;
        }
        console.warn('Share failed, using fallback:', error);
      }
    }

    try {
      const response = await fetch(currentImageUrl, { mode: 'cors' });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = currentImageName || 'photo.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(currentImageUrl, '_blank');
    }
  }, [currentImageName, currentImageUrl, isMobile]);

  const handleShare = useCallback(async () => {
    if (!canShare || !currentImageUrl) return;

    try {
      const resolvedTitle =
        shareTitle || currentImageName || t('common.shared_photo', 'Shared photo');
      await navigator.share({
        url: currentImageUrl,
        title: resolvedTitle,
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Share failed:', error);
      }
    }
  }, [canShare, currentImageName, currentImageUrl, shareTitle, t]);

  const handleBackdropClick = useCallback(
    (event) => {
      if (
        event.target === containerRef.current &&
        scaleRef.current <= 1 &&
        !isDraggingRef.current
      ) {
        requestClose('backdrop');
      }
    },
    [containerRef, requestClose]
  );

  const handleControlsFocus = useCallback(() => {
    setShowControls(true);
  }, []);

  const resetGestureState = useCallback(() => {
    setIsDraggingSync(false);
    pinchRef.current.active = false;
    pinchRef.current.startDistance = 0;
    dragModeRef.current = null;
    velocityRef.current = { x: 0, y: 0 };
    movedRef.current = false;
    pendingTapRef.current = false;
    suppressClickUntilRef.current = 0;
    activePointerIdRef.current = null;
    setDragOffset({ x: 0, y: 0 });
    setBackdropOpacity(1);
  }, [setIsDraggingSync]);

  useEffect(() => {
    if (isOpen) return;

    isClosingRef.current = false;
    setIsClosing(false);
    lastTapRef.current = 0;
    pendingTapRef.current = false;
    resetGestureState();

    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
    if (doubleTapTimeoutRef.current) {
      window.clearTimeout(doubleTapTimeoutRef.current);
      doubleTapTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, [isOpen, resetGestureState]);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        window.clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
      if (doubleTapTimeoutRef.current) {
        window.clearTimeout(doubleTapTimeoutRef.current);
        doubleTapTimeoutRef.current = null;
      }
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleTouchCancel = useCallback(() => {
    resetGestureState();
  }, [resetGestureState]);

  const handlePointerDown = useCallback(
    (event) => {
      if (isClosingRef.current) return;
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      activePointerIdRef.current = event.pointerId;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Ignore capture failures for elements without capture support.
      }
      handleDragStart(event.clientX, event.clientY);
    },
    [handleDragStart]
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (event.pointerType === 'touch') return;
      if (activePointerIdRef.current !== event.pointerId) return;

      handleDragMove(event.clientX, event.clientY);
    },
    [handleDragMove]
  );

  const handlePointerUp = useCallback(
    (event) => {
      if (event.pointerType === 'touch') return;
      if (activePointerIdRef.current !== event.pointerId) return;

      activePointerIdRef.current = null;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Ignore release errors for already-released captures.
      }
      handleDragEnd();
      pendingTapRef.current = false;
    },
    [handleDragEnd]
  );

  const handlePointerCancel = useCallback(
    (event) => {
      if (event.pointerType === 'touch') return;
      if (activePointerIdRef.current !== event.pointerId) return;

      activePointerIdRef.current = null;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Ignore release errors for already-released captures.
      }
      resetGestureState();
      pendingTapRef.current = false;
    },
    [resetGestureState]
  );

  if (!isOpen) return null;

  const chromeOpacity = isChromeHidden ? 0 : backdropOpacity;

  const modalContent = (
    <div
      ref={containerRef}
      className={styles.backdrop}
      style={{
        '--backdrop-opacity': backdropOpacity,
      }}
      data-controls-visible={isChromeHidden ? 'false' : 'true'}
      onClick={handleBackdropClick}
      onKeyDownCapture={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || t('common.image_preview', 'Image preview')}
      tabIndex={-1}
    >
      <header
        ref={topBarRef}
        className={`${styles.topBar} ${showControls ? styles.visible : ''}`}
        style={{ opacity: chromeOpacity }}
        onFocusCapture={handleControlsFocus}
        onPointerDown={handleControlPointerDown}
        {...chromeA11yProps}
      >
        <button
          className={styles.closeButton}
          type="button"
          data-modal-close
          onClick={() => requestClose('close-button')}
          aria-label={t('common.close', 'Close')}
          tabIndex={chromeTabIndex}
        >
          <X size={22} />
        </button>

        {headerContent && <div className={styles.headerContent}>{headerContent}</div>}

      </header>

      {!isZoomed && hasMultipleImages && currentIndex > 0 && (
        <button
          ref={navPrevRef}
          className={`${styles.navButton} ${styles.navPrev} ${
            showControls ? styles.navVisible : ''
          }`}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goToPrev();
          }}
          onFocusCapture={handleControlsFocus}
          onPointerDown={handleControlPointerDown}
          aria-label={t('common.previous', 'Previous')}
          tabIndex={chromeTabIndex}
          {...chromeA11yProps}
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {!isZoomed && hasMultipleImages && currentIndex < imageList.length - 1 && (
        <button
          ref={navNextRef}
          className={`${styles.navButton} ${styles.navNext} ${
            showControls ? styles.navVisible : ''
          }`}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            goToNext();
          }}
          onFocusCapture={handleControlsFocus}
          onPointerDown={handleControlPointerDown}
          aria-label={t('common.next', 'Next')}
          tabIndex={chromeTabIndex}
          {...chromeA11yProps}
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div className={`${styles.imageStage} ${isClosing ? styles.closing : ''}`}>
        <div
          className={`${styles.imageContainer} ${isDragging ? styles.dragging : ''} ${
            isClosing ? styles.closing : ''
          } ${isZoomed ? styles.zoomed : ''}`}
          style={{
            '--drag-x': `${dragOffset.x}px`,
            '--drag-y': `${dragOffset.y}px`,
            '--pan-x': `${panOffset.x}px`,
            '--pan-y': `${panOffset.y}px`,
            '--scale': scale,
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          onClick={handleDoubleTap}
        >
          {!imageLoaded && (
            <div className={styles.imageSkeleton}>
              <div className={styles.skeletonPulse} />
            </div>
          )}
          <img
            ref={imageRef}
            src={currentImageUrl}
            alt={currentImageName || t('common.full_image', 'Full size image')}
            className={`${styles.image} ${imageLoaded ? styles.loaded : ''}`}
            draggable={false}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
          />
        </div>
      </div>

      <footer
        ref={bottomBarRef}
        className={`${styles.bottomBar} ${showControls ? styles.visible : ''}`}
        style={{ opacity: chromeOpacity }}
        onFocusCapture={handleControlsFocus}
        onPointerDown={handleControlPointerDown}
        {...chromeA11yProps}
      >
        {hasMultipleImages && imageList.length <= 10 && (
          <div className={styles.pagination}>
            {imageList.map((_, idx) => (
              <button
                key={idx}
                className={`${styles.dot} ${idx === currentIndex ? styles.dotActive : ''}`}
                type="button"
                onClick={() => {
                  setCurrentIndex(idx);
                  setShowControls(true);
                }}
                aria-label={`${t('common.image', 'Image')} ${idx + 1}`}
                aria-current={idx === currentIndex}
                tabIndex={chromeTabIndex}
              />
            ))}
          </div>
        )}

        <div className={styles.actions}>
          {canShare && (
            <button
              className={styles.actionButton}
              type="button"
              onClick={handleShare}
              aria-label={t('common.share', 'Share')}
              tabIndex={chromeTabIndex}
            >
              <Share2 size={22} />
            </button>
          )}

          <button
            className={styles.primaryButton}
            type="button"
            onClick={handleDownload}
            aria-label={isMobile ? t('common.save', 'Save') : t('common.download', 'Download')}
            tabIndex={chromeTabIndex}
          >
            <Download size={18} />
            <span>{isMobile ? t('common.save', 'Save') : t('common.download', 'Download')}</span>
          </button>

          {footerActionNodes}
        </div>
      </footer>

    </div>
  );

  const portalTarget =
    typeof document !== 'undefined'
      ? document.getElementById('modal-root') || document.body
      : null;

  return portalTarget ? createPortal(modalContent, portalTarget) : modalContent;
};

export default ImagePreviewModal;
